'use client'

import * as React from 'react'
import { useTabsStore } from '../store'
import { $breadcrumbCta, PreviewButton } from './preview-button'
import {
  Breadcrumb,
  BreadcrumbEllipsisDropdown,
  BreadcrumbEllipsisDropdownItem,
  BreadcrumbItem,
} from '~/sotano-kit/primitives/breadcrumb'
import { Block, Commit } from '@basehub/data/replicache/index'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import RelevantLinks from './relevant-links'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { VariantsSwitcher } from './variants-switcher'
import {
  useIsExternalVisitor,
  useIsGuestInHisBranch,
} from '~/hooks-data/use-user-role'
import { useTimeDiff } from '~/hooks/use-time-diff'
import isEqual from 'lodash/isEqual'
import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { ConnectAIAgentInstructionsModal } from './ai-agent-instructions'

export type BreadcrumbBlock = Pick<
  Block,
  'id' | 'sourceBlockId' | 'title' | 'deletedAt' | 'type'
>

export const BreadcrumbDashboard = ({
  activeBlockId,
  activeCommitId,
  type,
  treeManager: _treeManager,
}: {
  type: 'editor' | 'editor-diff' | 'timeline'
  treeManager?: TreeManager<Block, NonNullable<unknown>>
} & (
  | { activeBlockId?: Block['id']; activeCommitId?: never }
  | { activeCommitId: Commit['id']; activeBlockId?: never }
)) => {
  const addTab = useTabsStore((s) => s.addTab)
  const isExternalVisitor = useIsExternalVisitor()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const hasNoAccess = React.useMemo(
    () => isExternalVisitor && !isGuestInHisBranch,
    [isExternalVisitor, isGuestInHisBranch]
  )

  // if path is longer than 3, we will show ellipsis from the second element to the second to last
  // e.g. ['a', 'b', 'c', 'd', 'e'] => ['a', '...', 'd', 'e']
  const parsedBlockPathData = useWorkingTreeStore(
    (s) => {
      const treeManager = _treeManager || s.tree
      const withPaths = false

      const strip = <N extends TreeNode<Block> | null | undefined>(
        node: N
      ): N extends TreeNode<Block> ? BreadcrumbBlock : null => {
        if (!node) {
          return null as N extends TreeNode<Block> ? BreadcrumbBlock : null
        }
        return {
          id: node.value.id,
          title: node.value.title,
          deletedAt: node.value.deletedAt,
          sourceBlockId: node.value.sourceBlockId,
          type: node.value.type,
        } as N extends TreeNode<Block> ? BreadcrumbBlock : null
      }

      if (!activeBlockId) {
        return { treeManager, hasEllipsis: false, path: [], block: undefined }
      }

      const block = strip(treeManager?.getNode(activeBlockId))
      const path = treeManager
        ?.getPath(block?.id, {
          format: 'array',
          ignoreRoot: true,
        })
        ?.map((id) => strip(treeManager?.getNode(id, { required: true })))

      if (!block || !path) {
        return { treeManager, hasEllipsis: false, path: [], block: undefined }
      }
      if (withPaths && path.length > 3) {
        return {
          treeManager,
          hasEllipsis: true,
          path: [path[0]!, '...', ...path.slice(-2)],
          hiddenPath: path.slice(1, -2),
          block,
        }
      }
      return { treeManager, hasEllipsis: false, path, block }
    },
    (prev, curr) => {
      const { treeManager: _, ...prevRest } = prev
      const { treeManager: __, ...currRest } = curr
      return isEqual(prevRest, currRest)
    }
  )

  if (activeCommitId) {
    return (
      <BreadcrumbStickyContainer>
        <Breadcrumb>
          <BreadcrumbItem active>{activeCommitId}</BreadcrumbItem>
        </Breadcrumb>
      </BreadcrumbStickyContainer>
    )
  }

  if (type === 'timeline') {
    return (
      <BreadcrumbStickyContainer>
        <Breadcrumb>
          <BreadcrumbItem active>Timeline</BreadcrumbItem>
        </Breadcrumb>
        <RelevantLinks />
      </BreadcrumbStickyContainer>
    )
  }

  return (
    <BreadcrumbStickyContainer>
      <Breadcrumb key={`breadcrumb-${activeBlockId}`}>
        {parsedBlockPathData.path.map((blockOrId) => {
          if (parsedBlockPathData.hasEllipsis && blockOrId === '...') {
            return (
              <BreadcrumbEllipsisDropdown key="ellipsis" sideOffset={4}>
                {parsedBlockPathData.hiddenPath?.map((block) => {
                  return (
                    <BreadcrumbEllipsisDropdownItem
                      key={block.id}
                      onClick={() =>
                        addTab({ key: block.id, partiallyOpen: true })
                      }
                    >
                      {block.title || 'Untitled'}
                      {block.deletedAt ? (
                        <>
                          &nbsp;
                          <DeletedNote deletedAt={block.deletedAt} />
                        </>
                      ) : (
                        ''
                      )}
                    </BreadcrumbEllipsisDropdownItem>
                  )
                })}
              </BreadcrumbEllipsisDropdown>
            )
          }

          const block = blockOrId as BreadcrumbBlock

          return (
            <BreadcrumbItem
              active={idToSourceBlockId(block) === activeBlockId}
              key={idToSourceBlockId(block)}
              onClick={() =>
                addTab({
                  key: idToSourceBlockId(block),
                  partiallyOpen: true,
                })
              }
            >
              {block.title || 'Untitled'}
              {block.deletedAt ? (
                <>
                  &nbsp;
                  <DeletedNote deletedAt={block.deletedAt} />
                </>
              ) : (
                ''
              )}
            </BreadcrumbItem>
          )
        })}
      </Breadcrumb>
      <div className="flex gap-x-4 items-center">
        {parsedBlockPathData.block &&
          parsedBlockPathData.block.type !== 'agent' &&
          parsedBlockPathData.treeManager && (
            <>
              {!hasNoAccess && (
                <VariantsSwitcher
                  blockId={parsedBlockPathData.block.id}
                  treeManager={parsedBlockPathData.treeManager}
                />
              )}
              <PreviewButton
                blockId={parsedBlockPathData.block.id}
                key={parsedBlockPathData.block.id}
                readOnly={type === 'editor-diff'}
                treeManager={parsedBlockPathData.treeManager}
              />
            </>
          )}
        {parsedBlockPathData.block &&
          parsedBlockPathData.block.type === 'agent' && (
            <>
              <PendingChangesIndicator block={parsedBlockPathData.block} />
              <ConnectAIAgentInstructionsModal
                block={parsedBlockPathData.block}
              />
            </>
          )}
      </div>
    </BreadcrumbStickyContainer>
  )
}

export const BreadcrumbStickyContainer = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <div className="sticky bg-dark-shade z-50 top-6 left-0 w-full border-b border-dark-border-solid text-dark-muted text-xs flex justify-between items-stretch h-[var(--editor-breadcrumb-height)] pr-2.5">
      {children}
    </div>
  )
}

const DeletedNote = ({ deletedAt }: { deletedAt: string }) => {
  const timeDiff = useTimeDiff(new Date(deletedAt), { short: false })
  return <span className="italic text-red-diff">deleted {timeDiff}</span>
}

const PendingChangesIndicator = ({ block }: { block: BreadcrumbBlock }) => {
  const { diff } = useWorkingDiff()
  const isChanged = useTreeSlice(
    // @ts-expect-error
    diff?.changesTree,
    (tm) => {
      const node = tm.getNode(idToSourceBlockId(block))
      return node && node?.value.status !== 'unmodified'
    },
    'reference',
    [block.id]
  )
  const addTab = useTabsStore((s) => s.addTab)
  const { repoSlug, teamSlug, repoPage, branchOrCommit } = useDashboardSlugs()

  if (!isChanged) return null
  return (
    <ButtonLink
      unstyled
      focusStyled
      className={$breadcrumbCta({
        className:
          '!text-dark-control-base hover:!text-dark-control-highlight group',
      })}
      onClick={() =>
        addTab({
          key: `diff:${idToSourceBlockId(block)}`,
          partiallyOpen: true,
        })
      }
      href={`/${teamSlug}/${repoSlug}/${repoPage}/${branchOrCommit}/diff:${idToSourceBlockId(block)}`}
    >
      <span className="bg-dark-control-base group-hover:bg-dark-control-highlight transition-colors size-1.5 rounded-full" />
      <span>Pending changes</span>
    </ButtonLink>
  )
}
